import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import ListItem from "@/components/ListItem";
import { Reference, useMutation } from "@apollo/client";
import { DELETE_EMPLOYMENT_MUTATION } from "../../person/mutations";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";

function formatDateRange(startDate?: string, endDate?: string): string | undefined {
  if (!startDate && !endDate) return undefined;
  if (startDate && endDate) return `${startDate} – ${endDate}`;
  return startDate || endDate;
}

interface DeleteEmploymentModalProps {
  open: boolean;
  onClose: () => void;
  employmentId: string;
  facilityName?: string;
  jobTitle?: string;
  startDate?: string;
  endDate?: string;
}

export default function DeleteEmploymentModal({
  open,
  onClose,
  employmentId,
  facilityName,
  jobTitle,
  startDate,
  endDate
}: DeleteEmploymentModalProps) {
  const { setError, clearError } = useErrorBanner();
  const [deleteEmployment, { loading }] = useMutation(DELETE_EMPLOYMENT_MUTATION, {
    update: (cache, { data }) => {
      if (!data?.deleteEmployment.employment.person) {
        return;
      }

      cache.modify({
        id: cache.identify(data.deleteEmployment.employment.person),
        fields: {
          employments(cachedEmployments, { readField }) {
            return cachedEmployments.filter((ref: Reference) => readField("id", ref) !== employmentId);
          }
        }
      });
    },

    onCompleted: (data) => {
      if (data.deleteEmployment.success) {
        clearError();
        showSnackbar("Employment deleted");
        onClose();
      } else {
        setError(data.deleteEmployment.message);
      }
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  const handleDelete = async () => {
    await deleteEmployment({
      variables: {
        id: employmentId
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Delete Employment"
      actions={
        <div className="flex gap-2">
          <Button variant="text" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            trailingIcon={false}
            icon="delete"
            onClick={handleDelete}
            disabled={loading}
            data-testid="confirm-delete-employment"
          >
            Delete
          </Button>
        </div>
      }
    >
      <div>
        <p>Are you sure you want to delete this employment? This cannot be undone.</p>
        {facilityName && (
          <div className="mt-4">
            <ListItem
              iconName="business_center"
              iconBackground
              headline={facilityName}
              supportingText={[jobTitle, formatDateRange(startDate, endDate)].filter(Boolean).join("\n") || undefined}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
